import React, { FC, useMemo } from 'react';
import { Select, RadioOption } from '@td-client-frontend/common';
import { Lock } from '@td-client-frontend/icons';
import { IncentiveCheckboxes } from './IncentiveCheckboxes';
import clsx from 'clsx';
import { healthBenefitLanguageOptions, marketingLanguageOptions, permToTestOptions } from './constants';
import { TabType, MarketingProfileById, PreferenceSettings } from '../../../../../@types/Marketing';
import { useAuth } from '../../../../../context/AuthContext';
import { Permissions } from '../../../../../@types/Permissions';

interface Props {
  profileData: MarketingProfileById;
  onChange: (data: { preferences: PreferenceSettings }) => void;
  isReadOnly?: boolean;
}

export const PreferencesForm: FC<Props> = ({ profileData, onChange, isReadOnly = false }) => {
  const {
    settings: {
      preferences: { communicationLanguage, permissionToTest, healthBenefitLanguage, incentive }
    }
  } = profileData;

  const { checkPerm } = useAuth();

  const isDisabled: boolean = useMemo(() => {
    const hasAllCreate: boolean = checkPerm([
      Permissions.MarketingProfileRegistrationCreate,
      Permissions.MarketingProfilePreferencesCreate,
      Permissions.MarketingProfileRetentionCreate
    ]);
    return (
      isReadOnly ||
      (!hasAllCreate
        ? profileData.default
          ? !(
              checkPerm([Permissions.MarketingProfileBaseWrite]) &&
              checkPerm([Permissions.MarketingProfilePreferencesWrite])
            )
          : !checkPerm([Permissions.MarketingProfilePreferencesWrite])
        : false)
    );
  }, [checkPerm, isReadOnly, profileData.default]);

  const handleChange = (fieldName: string) => (value: any) =>
    onChange({
      preferences: {
        ...profileData.settings.preferences,
        [fieldName]: value
      }
    });

  return (
    <div className="flex mt-4 mb-4 px-4 sm:px-0 md:px-4" data-testid={TabType.Preferences}>
      <div className="flex flex-col w-80 mr-8 sm:mr-4 md:mr-8">
        <label className="text-lg text-gray-60" htmlFor="marketingLang">
          Language for marketing communication
        </label>
        <Select
          name="marketingLang"
          options={marketingLanguageOptions}
          value={communicationLanguage}
          onChange={handleChange('communicationLanguage')}
          isDisabled={isDisabled}
          rightIcon={
            isDisabled && (
              <div className="h-full flex items-center justify-center w-12 shadow-field">
                <Lock />
              </div>
            )
          }
        />

        <label className="text-lg text-gray-60" htmlFor="healthBenefitLanguage">
          Preferred Health Benefit Language
        </label>
        <Select
          name="healthBenefitLanguage"
          options={healthBenefitLanguageOptions}
          value={healthBenefitLanguage}
          onChange={handleChange('healthBenefitLanguage')}
          isDisabled={isDisabled}
          rightIcon={
            isDisabled && (
              <div className="h-full flex items-center justify-center w-12 shadow-field">
                <Lock />
              </div>
            )
          }
        />

        <label className="text-lg text-gray-60" htmlFor="permToTest">
          Permission to test
        </label>
        <div className="space-y-3">
          <div className="flex items-center justify-start space-x-2 text-sm tracking-wide font-effra text-black-default">
            {permToTestOptions.options.map(({ value, label: optionLabel }) => {
              const input = {
                type: 'radio',
                checked: permissionToTest === value,
                disabled: isDisabled
              };
              return (
                <RadioOption
                  key={`permToTest.${optionLabel}`}
                  name="permToTest"
                  label={optionLabel}
                  value={value}
                  input={input}
                  onChange={() => handleChange('permissionToTest')(value)}
                  labelClassName="group flex items-center justify-center space-x-3 cursor-pointer font-effra text-lg text-gray-65"
                  checkboxClassName={clsx(
                    'flex mr-3 h-5 w-5',
                    input.disabled ? 'bg-gray-10 ' : 'hover:border-blue-80',
                    input.checked && !isDisabled && 'border-blue-80'
                  )}
                  activeCheckBoxClassName={isDisabled ? 'bg-gray-20' : 'bg-blue-80'}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex flex-col w-80">
        <label className="text-lg text-gray-60" htmlFor="incentive">
          Incentive
        </label>
        <div id="incentive" className="mt-2">
          <IncentiveCheckboxes isDisabled={isDisabled} incentiveData={incentive} onChange={handleChange('incentive')} />
        </div>
      </div>
    </div>
  );
};
